import { resolveIngress } from './ingress';
import { refusedBusinesses } from './overview';
import { ConsoleState, findRun, liveRevision, publishBlockers, routeSamples } from './store';
import { PublishBlocker, RouteDef } from './types';

export type SetupStepId = 'start' | 'sign-in' | 'shops' | 'check' | 'live';

export interface SetupStep {
  id: SetupStepId;
  title: string;
  done: boolean;
  /** What is still missing, in words a non-developer understands. */
  todo?: string;
}

export const SETUP_TITLES: Record<SetupStepId, string> = {
  start: 'Start',
  'sign-in': 'How they prove who they are',
  shops: 'Which shops send bills',
  check: 'Check with a real bill',
  live: 'Start receiving bills',
};

const vendorRoutes = (state: ConsoleState, vendorId: string): RouteDef[] =>
  state.routes.filter((route) => route.vendorId === vendorId);

const checked = (state: ConsoleState, route: RouteDef): boolean => {
  const run = findRun(state, route.id, 'shared');
  return Boolean(
    route.canarySampleId &&
    run &&
    run.total > 0 &&
    run.passed === run.total &&
    !publishBlockers(state, route.id, 'shared').some((blocker) => blocker.code.startsWith('tests-')),
  );
};

/** Branches of this vendor's shops that would have every request refused. */
export const blockedBranches = (state: ConsoleState, vendorId: string) =>
  (state.vendors.find((item) => item.id === vendorId)?.tenantIds ?? []).flatMap((tenantId) =>
    refusedBusinesses(state, vendorId, tenantId),
  );

export const vendorSetupSteps = (state: ConsoleState, vendorId: string): SetupStep[] => {
  const vendor = state.vendors.find((item) => item.id === vendorId);
  const routes = vendorRoutes(state, vendorId);
  const step = (id: SetupStepId, done: boolean, todo: string): SetupStep => ({
    id,
    title: SETUP_TITLES[id],
    done,
    ...(done ? {} : { todo }),
  });

  return [
    step('start', Boolean(vendor) && routes.length > 0, 'Choose a vendor to start from, or start empty.'),
    step(
      'sign-in',
      routes.length > 0 &&
        routes.every((route) => !route.signInInAddress && Boolean(resolveIngress(state, route).authSchemeId)),
      routes.some((route) => route.signInInAddress)
        ? 'Some endpoints take their key in the address. Ask a developer how they sign in.'
        : 'Choose how the vendor sends its key.',
    ),
    step(
      'shops',
      (vendor?.tenantIds.length ?? 0) > 0 && blockedBranches(state, vendorId).length === 0,
      (vendor?.tenantIds.length ?? 0) > 0
        ? 'Some branches cannot send anything yet. Let them use what this vendor sends.'
        : 'Choose at least one shop.',
    ),
    step(
      'check',
      routes.length > 0 && routes.every((route) => checked(state, route)),
      'Test each endpoint with one real bill on its Test page.',
    ),
    step(
      'live',
      routes.length > 0 && routes.every((route) => Boolean(liveRevision(state, route.id, 'shared'))),
      'Press "Start receiving bills".',
    ),
  ];
};

/** Which step fixes a blocker, and what to tell a person who has never seen the word "canonical". */
export const plainBlocker = (blocker: PublishBlocker): { step: SetupStepId; message: string } => {
  switch (blocker.code) {
    case 'no-samples':
      return { step: 'check', message: 'No real bill has been checked yet.' };
    case 'no-schema':
      return { step: 'start', message: "Casa's bill format is not chosen. Start from a vendor that works." };
    case 'no-sink':
      return { step: 'live', message: blocker.message };
    case 'tests-not-run':
      return { step: 'check', message: 'Something changed since the last check. Check the bill again.' };
    case 'tests-failing':
      return { step: 'check', message: 'The checked bill does not pass yet.' };
    case 'no-address':
    case 'address-locked':
      return { step: 'start', message: blocker.message };
    case 'sign-in-in-address':
      return { step: 'sign-in', message: blocker.message };
    case 'kinds-unsplit':
    case 'kind-incomplete':
      return { step: 'check', message: blocker.message };
    default:
      return { step: 'check', message: 'The field matching is not finished. Ask a developer to look at it.' };
  }
};

export const setupSampleCount = (state: ConsoleState, routeId: string): number =>
  routeSamples(state, routeId, 'shared').length;
